import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MovingBorderWrapper } from "@/components/ui/moving-border";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema } from "@/lib/jsonld";

// Images
import heroImage from "@/assets/usecases/createurs/hero.webp";
import feature1Image from "@/assets/usecases/createurs/feature-1.webp";
import feature2Image from "@/assets/usecases/createurs/feature-2.webp";
import feature3Image from "@/assets/usecases/createurs/feature-3.webp";

const features = [
  {
    number: "1",
    title: "Du contenu réservé aux abonnés",
    text: "Proposez un contenu exclusif accessible uniquement via la carte Wallet. Une vidéo bonus, un épisode de podcast inédit, un guide PDF gratuit, ou un code promo pour votre boutique. Annoncez-le sur vos canaux principaux et faites de la carte Wallet le seul moyen d'y accéder. Résultat : vos vrais fans l'ajoutent immédiatement.",
    image: feature1Image,
    imagePosition: "left" as const,
  },
  {
    number: "2",
    title: "L'accès prioritaire réservé",
    text: "Vous sortez un nouveau produit, une formation, vous ouvrez des places pour un coaching ? Prévenez d'abord ceux qui ont votre carte 24 heures avant tout le monde. Ça crée l'effet \"je ne veux rien rater\" et ça montre clairement que votre carte Wallet donne accès aux meilleures opportunités en premier.",
    image: feature2Image,
    imagePosition: "right" as const,
  },
  {
    number: "3",
    title: "Le badge d'entrée pour vos sessions en direct",
    text: "Faites de votre carte le billet d'entrée pour assister à vos lives, FAQ ou ateliers en ligne. Mettez le lien de connexion directement sur la carte. Résultat : plus d'inscriptions, et surtout, vos participants ont le lien sous les yeux au moment où ça commence. Fini les \"je ne trouve plus le lien\".",
    image: feature3Image,
    imagePosition: "left" as const,
  },
];

const badges = [
  "Votre nouveau produit",
  "Votre dernière vidéo",
  "Votre prochain événement",
];

export default function UseCasesCreateurs() {
  const jsonLdSchemas = [
    generateWebPageSchema("Créateurs & Influenceurs", seoConfig.useCasesCreateurs.description, "/cas-utilisation/createurs-influenceurs"),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Cas d'utilisation", path: "/#a-qui-sadresse" },
      { name: "Créateurs & Influenceurs", path: "/cas-utilisation/createurs-influenceurs" }
    ])
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO {...seoConfig.useCasesCreateurs} jsonLd={jsonLdSchemas} />
      <Header />

      {/* SECTION 1 - HERO */}
      <section className="relative pt-24 lg:pt-40 pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px] overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
            {/* Left: Text content */}
            <div className="max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-foreground mb-6">
                Le seul canal de communication que <span className="text-[#0c97a9]">les algorithmes ne contrôlent pas</span>
              </h1>

              <p className="leading-relaxed mb-8 font-medium text-[#4f5b5d] text-base sm:text-lg">
                Vous êtes créateur sur YouTube, TikTok ou Instagram et vous en avez marre de voir vos publications touchant à peine 5% de votre audience ? <span className="font-semibold text-[#0c97a9]">Unotif</span> transforme le Wallet mobile de vos abonnés en canal de diffusion personnel. Vous décidez. Vous contrôlez.
              </p>

              <div className="flex flex-col items-center lg:items-start">
                <MovingBorderWrapper duration={3000} borderRadius="0.5rem">
                  <Button asChild size="lg" className="bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-base px-8 py-6">
                    <a href="https://app.unotif.com/auth?mode=signup">
                      Créez votre première carte
                    </a>
                  </Button>
                </MovingBorderWrapper>
                <p className="text-sm text-muted-foreground mt-2">
                  👉 Sans engagement, sans carte bancaire
                </p>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="hidden lg:flex justify-center lg:justify-end items-center">
              <img
                src={heroImage}
                alt="Créateurs et influenceurs avec Unotif"
                loading="lazy"
                className="rounded-2xl max-w-full h-auto"
                style={{
                  boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 - Construisez votre canal */}
      <section className="bg-[#FAFBFB] pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-6">
              Construisez votre <span className="text-[#0c97a9]">canal de communication de secours</span>
            </h2>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d]">
              <span className="font-semibold text-[#0c97a9]">Unotif</span> ne remplace pas vos emails ou réseaux sociaux. Il ajoute une couche de sécurité à votre communication, augmentant drastiquement les chances que vos messages importants passent vraiment. La clé : donner à vos fans les plus engagés une vraie raison de s'abonner.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-12 sm:space-y-16 lg:space-y-20">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  feature.imagePosition === "right" ? "lg:grid-flow-dense" : ""
                }`}
              >
                {/* Image */}
                <div className={feature.imagePosition === "right" ? "lg:col-start-2" : ""}>
                  <img
                    src={feature.image}
                    alt={feature.title}
                    loading="lazy"
                    className="rounded-2xl w-full h-auto"
                    style={{
                      boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
                    }}
                  />
                </div>

                {/* Text */}
                <div className={feature.imagePosition === "right" ? "lg:col-start-1 lg:row-start-1" : ""}>
                  <div className="flex items-start gap-4">
                    <span className="text-4xl sm:text-5xl font-bold text-[#0c97a9]">
                      {feature.number}.
                    </span>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                        {feature.text}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Result text */}
          <div className="text-center mt-12 sm:mt-16 lg:mt-20 max-w-3xl mx-auto">
            <p className="text-base sm:text-lg font-semibold leading-relaxed text-[#074155]">
              Résultat ? Un canal direct vers vos abonnés les plus engagés, vous donnant une nouvelle chance (souvent la meilleure) de faire passer vos messages.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 - Votre audience vous attend */}
      <section className="pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-16 sm:pb-20 lg:pb-24">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-6">
              <span className="text-[#0c97a9]">Votre audience vous attend.</span> Ne laissez plus les algorithmes décider pour vous.
            </h2>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d] mb-4">
              Vous avez le choix. Continuer à construire votre activité sur un terrain qui ne vous appartient pas, où votre portée et vos revenus dépendent de plateformes qui n'ont pas vos intérêts à cœur.
            </p>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d] mb-10">
              Ou commencer à bâtir un actif qui vous appartient vraiment. Un canal de communication direct et privé qui vous garantit un lien permanent avec vos abonnés et clients les plus importants.
            </p>

            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6">
              {badges.map((badge, index) => (
                <span
                  key={index}
                  className="rounded-full border border-[#074155] bg-white px-4 py-2 text-sm sm:text-base font-medium text-[#074155]"
                >
                  {badge}
                </span>
              ))}
            </div>

            {/* Callout */}
            <p className="text-base sm:text-lg font-medium text-[#074155]">
              👀 Assurez-vous qu'ils le voient !
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 - CTA Final */}
      <section className="pb-16 sm:pb-20 lg:pb-24">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20">
          <Card className="max-w-2xl mx-auto text-center p-8 sm:p-10 lg:p-12 border border-gray-200 shadow-lg">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#074155] mb-4">
              Lancez votre nouveau canal de communication en 5 minutes !
            </h2>
            <p className="text-base sm:text-lg font-medium text-[#4f5b5d] mb-8">
              Découvrez la puissance d'un canal qui vous appartient, gratuitement.
            </p>
            <div className="flex flex-col items-center">
              <Button asChild size="lg" className="bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-base px-8 py-6">
                <a href="https://app.unotif.com/auth?mode=signup">
                  Commencer maintenant
                </a>
              </Button>
              <p className="text-sm text-muted-foreground mt-3">
                Sans carte bancaire
              </p>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}
